import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRoundIcon, PlugZapIcon } from 'lucide-react';
import { ErrorCode, PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CopyField } from '@/components/copy-field';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { SlotMeter } from '@/components/licence/slot-meter';
import { Stat } from '@/components/stat';
import { StatusChip } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, messageForError } from '@/lib/api-client';
import {
  activationsQuery,
  licenceKeys,
  licencesQuery,
  licensingApi,
  slotEventsQuery,
} from '@/lib/api/licensing';
import { sitesQuery } from '@/lib/api/sites';
import type { DomainVerification, Licence, LicenceActivation, LicenceSlots } from '@/lib/api/types';
import { formatDate, formatDateTime } from '@/lib/utils';

const STATUS_TONE: Record<string, 'active' | 'pending' | 'danger' | 'neutral' | 'info'> = {
  active: 'active',
  trial: 'info',
  pending_payment: 'pending',
  suspended: 'danger',
  expired: 'danger',
  cancelled: 'danger',
  revoked: 'danger',
  exhausted: 'danger',
  draft: 'neutral',
};

const FEATURE_LABELS: Record<string, string> = {
  visual_editor: 'Visual editor',
  scheduled_publish: 'Scheduled publishing',
  webhooks: 'Webhooks',
  offline_licence_files: 'Offline licence files',
  fingerprint_binding: 'Fingerprint binding',
  ip_allowlist: 'IP allowlist',
  multi_locale: 'Multiple locales',
  custom_roles: 'Custom roles',
  audit_export: 'Audit export',
  priority_support: 'Priority support',
};

interface SlotsExhausted {
  used: number;
  total: number;
}

/** `LICENCE_SLOTS_EXHAUSTED` carries the counts on `error.details`. */
function readExhausted(error: unknown): SlotsExhausted | null {
  if (!ApiError.is(error) || error.code !== ErrorCode.LICENCE_SLOTS_EXHAUSTED) return null;
  const details = error.details;
  if (!details || typeof details !== 'object') return null;
  const used = (details as { used?: unknown }).used;
  const total = (details as { total?: unknown }).total;
  if (typeof used !== 'number' || typeof total !== 'number') return null;
  return { used, total };
}

function LicencePage() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();

  const licences = useQuery(licencesQuery(orgId));
  const licence: Licence | null = licences.data?.[0] ?? null;

  const activations = useQuery(activationsQuery(orgId, licence?.id ?? null));
  const slotEvents = useQuery(slotEventsQuery(orgId, licence?.id ?? null));
  const sites = useQuery(sitesQuery(orgId));

  const [activateOpen, setActivateOpen] = useState(false);
  const [environmentId, setEnvironmentId] = useState('');
  const [deactivating, setDeactivating] = useState<LicenceActivation | null>(null);
  const [exhausted, setExhausted] = useState<SlotsExhausted | null>(null);
  const [verification, setVerification] = useState<DomainVerification | null>(null);
  const [activationSlots, setActivationSlots] = useState<LicenceSlots | null>(null);

  const environmentOptions = useMemo(
    () =>
      (sites.data ?? []).flatMap((site) =>
        site.environments.map((environment) => ({
          id: environment.id,
          label: `${site.name} · ${environment.name}`,
          type: environment.type,
          activated: environment.licenceActivationId !== null,
        })),
      ),
    [sites.data],
  );

  const environmentLabel = (activation: LicenceActivation): string => {
    const match = environmentOptions.find((option) => option.id === activation.siteEnvironmentId);
    return match?.label ?? activation.domain ?? 'Unknown installation';
  };

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: licenceKeys.all(orgId) });
    void queryClient.invalidateQueries({ queryKey: ['sites', orgId] });
  }

  const activateMutation = useMutation({
    mutationFn: () => licensingApi.activate(orgId, licence!.id, { siteEnvironmentId: environmentId }),
    onSuccess: (result) => {
      setActivationSlots(result.slots);
      if (result.status === 'pending_domain_verification' && result.domainVerification) {
        setVerification(result.domainVerification);
        toast.info('Activation created — the domain still needs verifying');
      } else {
        toast.success('Environment activated');
        setActivateOpen(false);
      }
      invalidate();
    },
    onError: (error) => {
      const counts = readExhausted(error);
      if (counts) {
        setExhausted(counts);
        return;
      }
      toast.error(messageForError(error, 'That environment could not be activated.'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (activationId: string) =>
      licensingApi.deactivate(orgId, licence!.id, { activationId }),
    onSuccess: (result) => {
      toast.success(
        result.slotReleased
          ? 'Activation released and the slot is free again'
          : result.releaseAt
            ? `Activation released — the slot frees on ${formatDate(result.releaseAt)}`
            : 'Activation released',
      );
      setDeactivating(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That activation could not be released.')),
  });

  if (licences.isLoading) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        <Skeleton className="h-16 w-72" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (licences.isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Operations" title="Licence" />
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(licences.error, 'The licence could not be loaded.')}
          {ApiError.is(licences.error) && licences.error.hint ? (
            <p className="mt-1 text-micro">{licences.error.hint}</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (!licence) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Operations" title="Licence" />
        <EmptyState
          icon={KeyRoundIcon}
          title="No licence on this organisation"
          description="A licence is what lets the SDK serve content to a production site. Without one, production environments run degraded and publishing has no effect on visitors."
        />
      </div>
    );
  }

  const enabledFeatures = Object.entries(licence.features).filter(([, enabled]) => enabled);
  const disabledFeatures = Object.entries(licence.features).filter(([, enabled]) => !enabled);
  const activeActivations = (activations.data ?? []).filter(
    (activation) => activation.status === 'active' || activation.status === 'pending_domain_verification',
  );

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Operations"
        title="Licence"
        description="What this organisation is entitled to, which installations are consuming it, and how much of the allowance is left."
        actions={
          <PermissionGate permission={PERMISSIONS.LICENCES_MANAGE}>
            <Button
              onClick={() => {
                setExhausted(null);
                setVerification(null);
                setActivationSlots(null);
                setEnvironmentId('');
                setActivateOpen(true);
              }}
            >
              <PlugZapIcon />
              Activate a site
            </Button>
          </PermissionGate>
        }
      />

      <section className="grid gap-6 rounded-lg border border-hairline bg-elevated px-6 py-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-title font-semibold tracking-tight text-ink">
              {licence.plan.name}
            </span>
            <StatusChip tone={STATUS_TONE[licence.status] ?? 'neutral'}>
              {licence.status.replace(/_/g, ' ')}
            </StatusChip>
            <Badge variant="outline" className="font-mono">
              {licence.kind}
            </Badge>
          </div>

          {licence.maskedKey ? (
            <CopyField label="Licence key" value={licence.maskedKey} />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Stat
              label={licence.kind === 'trial' ? 'Trial days left' : 'Days remaining'}
              value={licence.daysRemaining ?? '∞'}
              tone={
                licence.daysRemaining !== null && licence.daysRemaining <= 14 ? 'warning' : 'default'
              }
              hint={
                licence.expiresAt
                  ? `Expires ${formatDate(licence.expiresAt)}`
                  : 'No expiry date set'
              }
            />
            <Stat
              label="Renews"
              value={licence.renewalAt ? formatDate(licence.renewalAt) : '—'}
              hint={
                licence.graceUntil
                  ? `Grace period until ${formatDate(licence.graceUntil)}`
                  : `Runtimes revalidate every ${licence.validationIntervalHours}h`
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <SlotMeter slots={licence.slots} />
          {licence.slots.total !== null && licence.slots.used >= licence.slots.total ? (
            <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5">
              <p className="text-data font-medium text-danger">Every slot is in use</p>
              <p className="text-micro leading-relaxed text-danger">
                Release an activation below, or move to a plan with more production sites, before
                activating another.
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <span className="section-label">Installations</span>
            <CardTitle>
              {activeActivations.length} active{' '}
              {activeActivations.length === 1 ? 'activation' : 'activations'}
            </CardTitle>
            <CardDescription>
              Every installation that has claimed this licence. Only production activations consume
              a slot.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activations.isLoading ? (
              <div className="flex flex-col gap-2" aria-busy="true">
                {Array.from({ length: 3 }, (_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            ) : (activations.data?.length ?? 0) === 0 ? (
              <p className="text-data text-ink-muted">
                Nothing has activated against this licence yet. Activating a production environment
                is what lets its SDK serve published content.
              </p>
            ) : (
              <ul className="flex flex-col">
                {(activations.data ?? []).map((activation) => (
                  <li
                    key={activation.id}
                    className="flex flex-wrap items-center gap-3 border-b border-hairline py-3 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-data font-medium text-ink">
                        {environmentLabel(activation)}
                      </p>
                      <p className="truncate text-micro text-ink-subtle" data-numeric>
                        {activation.environmentType} · {activation.domain ?? 'no domain bound'} ·
                        activated {formatDate(activation.activatedAt)} ·{' '}
                        {activation.validationCount} validations
                      </p>
                    </div>
                    {activation.consumedSlot ? <Badge variant="accent">Uses a slot</Badge> : null}
                    <StatusChip
                      tone={
                        activation.status === 'active'
                          ? 'active'
                          : activation.status === 'pending_domain_verification'
                            ? 'pending'
                            : 'neutral'
                      }
                    >
                      {activation.status.replace(/_/g, ' ')}
                    </StatusChip>
                    {activation.status === 'active' ||
                    activation.status === 'pending_domain_verification' ? (
                      <PermissionGate permission={PERMISSIONS.LICENCES_MANAGE}>
                        <Button
                          variant="outlineDanger"
                          size="sm"
                          onClick={() => setDeactivating(activation)}
                        >
                          Deactivate
                        </Button>
                      </PermissionGate>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <span className="section-label">Entitlements</span>
              <CardTitle>What this plan includes</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-1.5">
                {enabledFeatures.map(([key]) => (
                  <Badge key={key} variant="success">
                    {FEATURE_LABELS[key] ?? key}
                  </Badge>
                ))}
              </div>
              {disabledFeatures.length > 0 ? (
                <div className="flex flex-col gap-1.5 border-t border-hairline pt-3">
                  <span className="section-label">Not on this plan</span>
                  <div className="flex flex-wrap gap-1.5">
                    {disabledFeatures.map(([key]) => (
                      <Badge key={key} variant="outline">
                        {FEATURE_LABELS[key] ?? key}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="section-label">Restrictions</span>
              <CardTitle>Where this licence may run</CardTitle>
              <CardDescription>
                A validation from outside these bounds is refused, and the runtime degrades rather
                than serving content.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 text-data">
              <RestrictionRow
                label="Domains"
                values={licence.allowedDomains}
                empty="Any domain"
              />
              <RestrictionRow label="IP addresses" values={licence.allowedIps} empty="Any address" />
              <RestrictionRow
                label="Environments"
                values={licence.allowedEnvironments}
                empty="Any environment"
              />
              <ul className="flex flex-col gap-1 border-t border-hairline pt-3 text-micro text-ink-muted">
                <li>
                  Domain verification:{' '}
                  {licence.requireDomainVerification ? 'required' : 'not required'}
                </li>
                <li>
                  Machine fingerprint: {licence.requireFingerprint ? 'required' : 'not required'}
                </li>
                <li>
                  Offline grace: {licence.offlineGraceHours}h after the last successful validation
                </li>
                <li>
                  Last validated:{' '}
                  <span data-numeric>
                    {licence.lastValidatedAt ? formatDateTime(licence.lastValidatedAt) : 'never'}
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader>
          <span className="section-label">Ledger</span>
          <CardTitle>Slot history</CardTitle>
          <CardDescription>
            Every change to the slot count, in order. Useful when an allowance looks wrong.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {slotEvents.isLoading ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-8 w-full" />
              ))}
            </div>
          ) : (slotEvents.data?.length ?? 0) === 0 ? (
            <p className="text-data text-ink-muted">No slot movements recorded yet.</p>
          ) : (
            <ol className="flex flex-col">
              {(slotEvents.data ?? []).map((event) => (
                <li
                  key={event.id}
                  className="flex flex-wrap items-center gap-3 border-b border-hairline py-2 last:border-b-0"
                >
                  <Badge variant={event.delta > 0 ? 'warning' : 'success'} className="font-mono">
                    {event.delta > 0 ? `+${event.delta}` : event.delta}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate text-data text-ink">
                    {event.event.replace(/_/g, ' ')}
                    {event.reason ? ` — ${event.reason}` : ''}
                  </span>
                  <span className="font-mono text-micro text-ink-subtle" data-numeric>
                    {event.slotsUsedAfter}
                    {event.slotsTotalAfter !== null ? ` / ${event.slotsTotalAfter}` : ''}
                  </span>
                  <span className="text-micro text-ink-subtle" data-numeric>
                    {formatDateTime(event.createdAt)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate a site environment</DialogTitle>
            <DialogDescription>
              Activation binds an environment to this licence. Production environments take a slot;
              staging and development do not.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="activate-env">Environment</Label>
              <Select value={environmentId} onValueChange={setEnvironmentId}>
                <SelectTrigger id="activate-env">
                  <SelectValue placeholder="Choose an environment" />
                </SelectTrigger>
                <SelectContent>
                  {environmentOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id} disabled={option.activated}>
                      {option.label}
                      {option.activated ? ' — already activated' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {environmentOptions.length === 0 ? (
                <p className="text-micro text-ink-subtle">
                  No environments yet.{' '}
                  <Link
                    to="/app/$orgId/sites"
                    params={{ orgId }}
                    className="text-accent underline-offset-2 hover:underline"
                  >
                    Create a site first
                  </Link>
                  .
                </p>
              ) : null}
            </div>

            {activationSlots ? (
              <p className="text-micro text-ink-muted" data-numeric>
                Slots now {activationSlots.used}
                {activationSlots.total !== null ? ` of ${activationSlots.total}` : ''}.
              </p>
            ) : null}

            {exhausted ? (
              <div className="flex flex-col gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5">
                <p className="text-data font-medium text-danger">
                  All {exhausted.total} slots are already in use ({exhausted.used} of{' '}
                  {exhausted.total})
                </p>
                <p className="text-micro leading-relaxed text-danger">
                  Deactivate a production environment you no longer publish to, or upgrade the plan
                  so this licence carries more production sites.
                </p>
                <div>
                  <PermissionGate permission={PERMISSIONS.BILLING_VIEW}>
                    <Button asChild size="sm">
                      <Link to="/app/$orgId/billing" params={{ orgId }}>
                        See plan options
                      </Link>
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            ) : null}

            {verification ? (
              <div className="flex flex-col gap-2 rounded-md border border-warning/35 bg-warning-soft px-3 py-2.5">
                <p className="text-data font-medium text-warning">
                  {verification.hostname} needs verifying before this activation goes live
                </p>
                <p className="text-micro leading-relaxed text-warning">
                  Add this {verification.method === 'dns_txt' ? 'DNS TXT record' : 'file'} at your
                  registrar. The activation flips to active once the record is seen.
                </p>
                <CopyField label="Record name" value={verification.recordName} />
                <CopyField label="Record value" value={verification.recordValue} truncate={false} />
              </div>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setActivateOpen(false)}>
              {verification ? 'Done' : 'Cancel'}
            </Button>
            <Button
              disabled={!environmentId || activateMutation.isPending}
              onClick={() => {
                setExhausted(null);
                activateMutation.mutate();
              }}
            >
              {activateMutation.isPending ? 'Activating…' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deactivating !== null}
        onOpenChange={(open) => !open && setDeactivating(null)}
        destructive
        title="Release this activation?"
        confirmLabel="Deactivate"
        pending={deactivateMutation.isPending}
        description={
          <>
            <strong className="text-ink">
              {deactivating ? environmentLabel(deactivating) : ''}
            </strong>{' '}
            stops validating against this licence, and its SDK drops into degraded mode on the next
            check. Whether the slot frees immediately depends on the plan's cooldown — a plan that
            delays the release discourages rotating one licence across many sites.
          </>
        }
        onConfirm={() => deactivating && deactivateMutation.mutate(deactivating.id)}
      />
    </div>
  );
}

function RestrictionRow({
  label,
  values,
  empty,
}: {
  label: string;
  values: string[];
  empty: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="section-label">{label}</span>
      {values.length === 0 ? (
        <span className="text-data text-ink-subtle">{empty}</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {values.map((value) => (
            <Badge key={value} variant="neutral" className="font-mono">
              {value}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/licence')({
  component: LicencePage,
});
